import type { AccountListItem } from '../../lib/types/finance.js';

/** Board's own 4-level naming, applied to real tree depth (derived from
 * `parentAccountId`, never from the raw `levelId` UUID — it is opaque). */
export const LEVEL_LABELS = ['گروه', 'کل', 'معین', 'تفصیلی'] as const;

export interface TreeNode extends AccountListItem {
  readonly depth: number;
  readonly children: TreeNode[];
}

export function buildTree(items: readonly AccountListItem[]): TreeNode[] {
  const byParent = new Map<string | null, AccountListItem[]>();
  for (const item of items) {
    const key = item.parentAccountId;
    const list = byParent.get(key) ?? [];
    list.push(item);
    byParent.set(key, list);
  }
  function attach(parentId: string | null, depth: number): TreeNode[] {
    const children = byParent.get(parentId) ?? [];
    return children
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((item) => ({ ...item, depth, children: attach(item.accountId, depth + 1) }));
  }
  return attach(null, 0);
}

/** Depth-first rows; a node's children appear only while it is expanded and within `depthLimit` levels. */
export function flattenTree(
  nodes: readonly TreeNode[],
  expanded: ReadonlySet<string>,
  depthLimit: number,
): TreeNode[] {
  const out: TreeNode[] = [];
  for (const node of nodes) {
    if (node.depth >= depthLimit) continue;
    out.push(node);
    if (node.children.length > 0 && expanded.has(node.accountId)) {
      out.push(...flattenTree(node.children, expanded, depthLimit));
    }
  }
  return out;
}

/** Ids of every node that has children and sits above `depth` (i.e. must be open to reveal `depth + 1` levels). */
export function expandableIds(nodes: readonly TreeNode[], belowDepth = Infinity): Set<string> {
  const ids = new Set<string>();
  function walk(list: readonly TreeNode[]) {
    for (const node of list) {
      if (node.children.length > 0 && node.depth < belowDepth) ids.add(node.accountId);
      walk(node.children);
    }
  }
  walk(nodes);
  return ids;
}

/** Splits a code into the parent's code (dimmed) and the digits this level adds (emphasised). */
export function splitCode(code: string, parentCode: string | undefined): [string, string] {
  if (parentCode && code.startsWith(parentCode) && code.length > parentCode.length) {
    return [parentCode, code.slice(parentCode.length)];
  }
  return ['', code];
}

export function matchesFilter(node: TreeNode, query: string, showInactive: boolean): boolean {
  if (!showInactive && node.status !== 'active') return false;
  if (!query) return true;
  const q = query.trim().toLowerCase();
  return node.code.toLowerCase().includes(q) || node.name.toLowerCase().includes(q);
}
